export interface FantasySettings {
    [key: string]: string | number | undefined;
}

export interface FantasyRosterPlayer {
    id: string;
    name: string;
    positionId: string;
    teamId: string;
    teamName: string;
    fantasySlot: string;
}

export interface RealPlay {
    jugada?: string;
    ofensiva?: string;
    QB?: string;
    RB?: string;
    WR?: string;
    K?: string;
    yds?: string;
    td?: string;
    int?: string;
    sack?: string;
    fumbleF?: string;
    fumbleR?: string;
    deftd?: string;
    ret?: string;
    retTd?: string;
}

export interface RealMatch {
    id: string;
    status: string;
    date: string;
    hour: string;

    local: {
        id: string;
        name: string;
        score: string;
    };

    visitor: {
        id: string;
        name: string;
        score: string;
    };

    plays: RealPlay[];
}


interface OffensiveStats {
    id: string;
    ydsP: number;
    tdP: number;
    int: number;
    ydsR: number;
    tdR: number;
    rec: number;
    ydsRec: number;
    tdRec: number;
    tdRet: number;
    pt: number;
    fum: number;
    fumL: number;
    pts: number;
}

interface KickerStats {
    id: string;
    pat: number;
    fg20: number;
    fg30: number;
    fg40: number;
    fg50: number;
    missed: number;
    pts: number;
}

interface DefenseStats {
    id: string;
    sack: number;
    int: number;
    fum: number;
    fumR: number;
    saf: number;
    td: number;
    ydsAgainst: number;
    pts: number;
}

export interface FantasyPlayerResult {
    id: string;
    name: string;
    teamId: string;
    teamName: string;
    positionId: string;
    position: string;
    slot: string;
    isStarter: boolean;
    points: number;
    statLine: string;
    stats: OffensiveStats | KickerStats | DefenseStats;
}

export interface FantasyTeamResult {
    totalPoints: number;
    starters: FantasyPlayerResult[];
    bench: FantasyPlayerResult[];
    allPlayers: FantasyPlayerResult[];
}

export interface FantasyGameCalculation {
    myTeam: FantasyTeamResult;
    opponentTeam: FantasyTeamResult;
}

/*
    Convierte valores vacíos, null o texto inválido en cero.
*/
function getNumber(value: unknown): number {
    const numberValue = Number.parseFloat(String(value ?? ""));

    return Number.isFinite(numberValue)
        ? numberValue
        : 0;
}

function hasValue(value: unknown): boolean {
    return String(value ?? "").trim() !== "";
}

function getSetting(
    settings: FantasySettings,
    property: string
): number {
    return getNumber(settings[property]);
}

function getYardPoints(
    yards: number,
    yardsPerPoint: number,
    pointsPerUnit: number
): number {
    if (yardsPerPoint === 0) {
        return 0;
    }

    return (yards / yardsPerPoint) * pointsPerUnit;
}

function getPositionName(positionId: string): string {
    const positions: Record<string, string> = {
        "1": "QB",
        "2": "RB",
        "3": "WR",
        "7": "K",
        "8": "DEF"
    };

    return positions[positionId] || "—";
}

/*
    Compatibilidad con el formato antiguo:

    "0" → Titular regular
    "1" → FLEX
    "2" → K o DEF
    "3" → Banca

    También permite los slots del formato nuevo:
    QB, RB, WR, FLEX, K, DEF y BN.
*/
function getSlotName(
    fantasySlot: string,
    positionId: string
): string {
    const slot = String(fantasySlot ?? "").toUpperCase();

    if (slot === "QB") {
        return "QB";
    }

    if (slot === "RB") {
        return "RB";
    }

    if (slot === "WR") {
        return "WR";
    }

    if (slot === "FLEX" || slot === "WB") {
        return "FLEX";
    }

    if (slot === "K") {
        return "K";
    }

    if (slot === "DEF") {
        return "DEF";
    }

    if (slot === "BN" || slot === "BENCH" || slot === "3") {
        return "BN";
    }

    if (slot === "1") {
        return "FLEX";
    }

    if (slot === "2") {
        return positionId === "8"
            ? "DEF"
            : "K";
    }

    return getPositionName(positionId);
}

function isStarter(
    fantasySlot: string
): boolean {
    const slot = String(fantasySlot ?? "").toUpperCase();

    return ![
        "3",
        "BN",
        "BENCH"
    ].includes(slot);
}

function createOffensiveStats(
    id: string
): OffensiveStats {
    return {
        id,
        ydsP: 0,
        tdP: 0,
        int: 0,
        ydsR: 0,
        tdR: 0,
        rec: 0,
        ydsRec: 0,
        tdRec: 0,
        tdRet: 0,
        pt: 0,
        fum: 0,
        fumL: 0,
        pts: 0
    };
}

function createKickerStats(
    id: string
): KickerStats {
    return {
        id,
        pat: 0,
        fg20: 0,
        fg30: 0,
        fg40: 0,
        fg50: 0,
        missed: 0,
        pts: 0
    };
}

function createDefenseStats(
    id: string
): DefenseStats {
    return {
        id,
        sack: 0,
        int: 0,
        fum: 0,
        fumR: 0,
        saf: 0,
        td: 0,
        ydsAgainst: 0,
        pts: 0
    };
}

/*
    Conserva la lógica existente de QB.
*/
function getQBStats(
    play: RealPlay,
    stats: Map<string, OffensiveStats>
): void {
    const playerId = String(play.QB ?? "");

    if (!playerId || !stats.has(playerId)) {
        return;
    }

    const player = stats.get(playerId);

    if (!player) {
        return;
    }

    if (hasValue(play.int)) {
        player.int += 1;
    } else if (hasValue(play.fumbleR)) {
        player.fumL += 1;
        player.fum += 1;
    } else if (hasValue(play.fumbleF)) {
        player.fum += 1;
    } else if (hasValue(play.yds)) {
        player.ydsP += getNumber(play.yds);

        if (play.td === "6") {
            player.tdP += 1;
        } else if (play.td === "2") {
            player.pt += 1;
        }
    }
}

/*
    Conserva la lógica existente de RB.
*/
function getRBStats(
    play: RealPlay,
    stats: Map<string, OffensiveStats>
): void {
    const playerId = String(play.RB ?? "");

    if (!playerId || !stats.has(playerId)) {
        return;
    }

    const player = stats.get(playerId);

    if (!player || !hasValue(play.yds)) {
        return;
    }

    player.ydsR += getNumber(play.yds);

    if (play.td === "6") {
        player.tdR += 1;
    } else if (play.td === "2") {
        player.pt += 1;
    } else if (hasValue(play.fumbleR)) {
        player.fumL += 1;
        player.fum += 1;
    } else if (hasValue(play.fumbleF)) {
        player.fum += 1;
    }
}

/*
    Conserva la lógica existente de WR.
*/
function getWRStats(
    play: RealPlay,
    stats: Map<string, OffensiveStats>
): void {
    const playerId = String(play.WR ?? "");

    if (!playerId || !stats.has(playerId)) {
        return;
    }

    const player = stats.get(playerId);

    if (!player || !hasValue(play.yds)) {
        return;
    }

    player.rec += 1;
    player.ydsRec += getNumber(play.yds);

    if (play.td === "6") {
        player.tdRec += 1;
    } else if (play.td === "2") {
        player.pt += 1;
    } else if (hasValue(play.fumbleR)) {
        player.fumL += 1;
        player.fum += 1;
    } else if (hasValue(play.fumbleF)) {
        player.fum += 1;
    }
}

function getReturnStats(
    play: RealPlay,
    stats: Map<string, OffensiveStats>
): void {
    const playerId = String(play.ret ?? "");

    if (!playerId || !stats.has(playerId)) {
        return;
    }

    const player = stats.get(playerId);

    if (!player) {
        return;
    }

    if (hasValue(play.retTd)) {
        player.tdRet += 1;
    } else if (hasValue(play.fumbleR)) {
        player.fumL += 1;
        player.fum += 1;
    } else if (hasValue(play.fumbleF)) {
        player.fum += 1;
    }
}

function getKickerStats(
    play: RealPlay,
    stats: Map<string, KickerStats>
): void {
    const playerId = String(play.K ?? "");

    if (!playerId || !stats.has(playerId)) {
        return;
    }

    const player = stats.get(playerId);

    if (!player || !hasValue(play.td)) {
        return;
    }

    if (play.td === "0") {
        player.missed += 1;
        return;
    }

    if (play.td === "1") {
        player.pat += 1;
        return;
    }

    const yards = getNumber(play.yds);

    if (yards > 49) {
        player.fg50 += 1;
    } else if (yards > 39) {
        player.fg40 += 1;
    } else if (yards > 29) {
        player.fg30 += 1;
    } else if (yards > 19) {
        player.fg20 += 1;
    }
}

function getDefenseTeamId(
    play: RealPlay,
    localTeamId: string,
    visitorTeamId: string
): string {
    if (play.ofensiva === localTeamId) {
        return visitorTeamId;
    }

    if (play.ofensiva === visitorTeamId) {
        return localTeamId;
    }

    return "";
}

/*
    Conserva la lógica de defensa y corrige únicamente el acumulado
    de yardas, que en el beta anterior se perdía al pasar números
    por valor entre funciones.
*/
function getDefenseStats(
    play: RealPlay,
    stats: Map<string, DefenseStats>,
    localTeamId: string,
    visitorTeamId: string
): void {
    const defenseTeamId = getDefenseTeamId(
        play,
        localTeamId,
        visitorTeamId
    );

    if (!defenseTeamId) {
        return;
    }

    if (
        play.jugada !== "2" &&
        hasValue(play.yds)
    ) {
        const yardageDefenseId =
            play.ofensiva === localTeamId
                ? visitorTeamId
                : localTeamId;

        const defense = stats.get(yardageDefenseId);

        if (defense) {
            defense.ydsAgainst += getNumber(play.yds);
        }
    }

    const defense = stats.get(defenseTeamId);

    if (!defense) {
        return;
    }

    if (hasValue(play.sack)) {
        defense.sack += 1;
    }

    if (hasValue(play.fumbleF)) {
        defense.fum += 1;
    }

    if (hasValue(play.int)) {
        defense.int += 1;
    } else if (hasValue(play.fumbleR)) {
        defense.fumR += 1;
    }

    if (hasValue(play.deftd)) {
        if (play.deftd === "2") {
            defense.saf += 1;
        } else {
            defense.td += 1;
        }
    }

    if (hasValue(play.retTd)) {
        defense.td += 1;
    }
}

function getDefensePointsAllowed(
    pointsAllowed: number,
    settings: FantasySettings
): number {
    if (pointsAllowed > 34) {
        return getSetting(settings, "pts35");
    }

    if (pointsAllowed > 27) {
        return getSetting(settings, "pts34");
    }

    if (pointsAllowed > 20) {
        return getSetting(settings, "pts27");
    }

    if (pointsAllowed > 13) {
        return getSetting(settings, "pts20");
    }

    if (pointsAllowed > 6) {
        return getSetting(settings, "pts13");
    }

    if (pointsAllowed > 0) {
        return getSetting(settings, "pts6");
    }

    return getSetting(settings, "pts0");
}

function calculateOffensivePoints(
    settings: FantasySettings,
    stats: OffensiveStats
): void {
    stats.pts +=
        getYardPoints(
            stats.ydsP,
            getSetting(settings, "passYds"),
            getSetting(settings, "passPoint")
        ) +
        stats.tdP * getSetting(settings, "passTds") +
        stats.int * getSetting(settings, "passInt") +
        getYardPoints(
            stats.ydsR,
            getSetting(settings, "rushYds"),
            getSetting(settings, "rushPoint")
        ) +
        stats.tdR * getSetting(settings, "rushTds") +
        stats.rec * getSetting(settings, "rec") +
        getYardPoints(
            stats.ydsRec,
            getSetting(settings, "recYds"),
            getSetting(settings, "recPoint")
        ) +
        stats.tdRec * getSetting(settings, "recTds") +
        stats.pt * getSetting(settings, "conv") +
        stats.fum * getSetting(settings, "fumble") +
        stats.fumL * getSetting(settings, "fumbleLost") +
        stats.tdRet * getSetting(settings, "retTds");
}

function calculateKickerPoints(
    settings: FantasySettings,
    stats: KickerStats
): void {
    stats.pts +=
        stats.pat * getSetting(settings, "extraPoint") +
        stats.fg20 * getSetting(settings, "fg20") +
        stats.fg30 * getSetting(settings, "fg30") +
        stats.fg40 * getSetting(settings, "fg40") +
        stats.fg50 * getSetting(settings, "fg50");
}

function calculateDefensePoints(
    settings: FantasySettings,
    stats: DefenseStats
): void {
    stats.pts +=
        stats.sack * getSetting(settings, "sack") +
        stats.int * getSetting(settings, "int") +
        stats.fum * getSetting(settings, "fumbleF") +
        stats.fumR * getSetting(settings, "fumbleRec") +
        stats.saf * getSetting(settings, "safety") +
        stats.td * getSetting(settings, "defTds");
}

function isMatchStarted(
    match: RealMatch
): boolean {
    return (
        match.status === "live" ||
        match.status === "final" ||
        match.plays.length > 0
    );
}

function getOffensiveStatLine(
    positionId: string,
    stats: OffensiveStats
): string {
    if (positionId === "1") {
        return `${stats.ydsP} YDS · ${stats.tdP} TD · ${stats.int} INT`;
    }

    if (positionId === "2") {
        return `${stats.ydsR} RYDS · ${stats.ydsRec} REYDS · ${stats.tdR + stats.tdRec} TD`;
    }

    return `${stats.rec} REC · ${stats.ydsRec} YDS · ${stats.tdRec} TD`;
}

function getKickerStatLine(
    stats: KickerStats
): string {
    const fieldGoals =
        stats.fg20 +
        stats.fg30 +
        stats.fg40 +
        stats.fg50;

    return `${fieldGoals} FG · ${stats.pat} XP`;
}

function getDefenseStatLine(
    stats: DefenseStats
): string {
    return `${stats.sack} SCK · ${stats.int} INT · ${stats.fum} FF`;
}

function calculateTeam(
    settings: FantasySettings,
    roster: FantasyRosterPlayer[],
    realMatches: RealMatch[]
): FantasyTeamResult {
    const offensiveStats = new Map<
        string,
        OffensiveStats
    >();

    const kickerStats = new Map<
        string,
        KickerStats
    >();

    const defenseStats = new Map<
        string,
        DefenseStats
    >();

    roster.forEach(function (player) {
        if (player.positionId === "7") {
            kickerStats.set(
                player.id,
                createKickerStats(player.id)
            );

            return;
        }

        if (player.positionId === "8") {
            defenseStats.set(
                player.id,
                createDefenseStats(player.id)
            );

            return;
        }

        offensiveStats.set(
            player.id,
            createOffensiveStats(player.id)
        );
    });

    realMatches.forEach(function (match) {
        match.plays.forEach(function (play) {
            if (play.jugada !== "2") {
                getQBStats(play, offensiveStats);
                getRBStats(play, offensiveStats);
                getWRStats(play, offensiveStats);
                getReturnStats(play, offensiveStats);
            } else {
                getKickerStats(play, kickerStats);
            }

            getDefenseStats(
                play,
                defenseStats,
                match.local.id,
                match.visitor.id
            );
        });

        /*
            Los puntos por defensiva y menos de 100 yardas sólo se
            asignan cuando el partido comenzó o terminó.
        */
        if (!isMatchStarted(match)) {
            return;
        }

        const localDefense = defenseStats.get(
            match.local.id
        );

        const visitorDefense = defenseStats.get(
            match.visitor.id
        );

        if (localDefense) {
            localDefense.pts += getDefensePointsAllowed(
                getNumber(match.visitor.score),
                settings
            );

            if (localDefense.ydsAgainst < 100) {
                localDefense.pts += getSetting(
                    settings,
                    "yds100"
                );
            }
        }

        if (visitorDefense) {
            visitorDefense.pts += getDefensePointsAllowed(
                getNumber(match.local.score),
                settings
            );

            if (visitorDefense.ydsAgainst < 100) {
                visitorDefense.pts += getSetting(
                    settings,
                    "yds100"
                );
            }
        }
    });

    offensiveStats.forEach(function (stats) {
        calculateOffensivePoints(settings, stats);
    });

    kickerStats.forEach(function (stats) {
        calculateKickerPoints(settings, stats);
    });

    defenseStats.forEach(function (stats) {
        calculateDefensePoints(settings, stats);
    });

    const allPlayers = roster
        .map(function (player): FantasyPlayerResult {
            const slot = getSlotName(
                player.fantasySlot,
                player.positionId
            );

            const starter = isStarter(
                player.fantasySlot
            );

            if (player.positionId === "7") {
                const stats = kickerStats.get(player.id) ||
                    createKickerStats(player.id);

                return {
                    id: player.id,
                    name: player.name,
                    teamId: player.teamId,
                    teamName: player.teamName,
                    positionId: player.positionId,
                    position: "K",
                    slot,
                    isStarter: starter,
                    points: stats.pts,
                    statLine: getKickerStatLine(stats),
                    stats
                };
            }

            if (player.positionId === "8") {
                const stats = defenseStats.get(player.id) ||
                    createDefenseStats(player.id);

                return {
                    id: player.id,
                    name: player.name,
                    teamId: player.teamId,
                    teamName: player.teamName,
                    positionId: player.positionId,
                    position: "DEF",
                    slot,
                    isStarter: starter,
                    points: stats.pts,
                    statLine: getDefenseStatLine(stats),
                    stats
                };
            }

            const stats = offensiveStats.get(player.id) ||
                createOffensiveStats(player.id);

            return {
                id: player.id,
                name: player.name,
                teamId: player.teamId,
                teamName: player.teamName,
                positionId: player.positionId,
                position: getPositionName(player.positionId),
                slot,
                isStarter: starter,
                points: stats.pts,
                statLine: getOffensiveStatLine(
                    player.positionId,
                    stats
                ),
                stats
            };
        })
        .sort(function (
            firstPlayer,
            secondPlayer
        ) {
            if (
                firstPlayer.isStarter !==
                secondPlayer.isStarter
            ) {
                return firstPlayer.isStarter
                    ? -1
                    : 1;
            }

            return firstPlayer.slot.localeCompare(
                secondPlayer.slot
            );
        });

    const starters = allPlayers.filter(function (player) {
        return player.isStarter;
    });

    const bench = allPlayers.filter(function (player) {
        return !player.isStarter;
    });

    const totalPoints = starters.reduce(
        function (total, player) {
            return total + player.points;
        },
        0
    );

    return {
        totalPoints,
        starters,
        bench,
        allPlayers
    };
}

/*
    Calcula los resultados Fantasy de un roster temporal dentro
    de un único partido real.

    Se usa en el detalle de partido para mostrar la producción de
    todos los jugadores elegibles de ambos equipos, sin distinguir
    titulares, banca o managers Fantasy.
*/
export function calculateFantasyMatchPlayers(
    settings: FantasySettings,
    players: FantasyRosterPlayer[],
    match: RealMatch
): FantasyPlayerResult[] {
    return calculateTeam(
        settings,
        players,
        [match]
    ).allPlayers;
}

export function calculateFantasyGame(
    settings: FantasySettings,
    myRoster: FantasyRosterPlayer[],
    opponentRoster: FantasyRosterPlayer[],
    realMatches: RealMatch[]
): FantasyGameCalculation {
    return {
        myTeam: calculateTeam(
            settings,
            myRoster,
            realMatches
        ),

        opponentTeam: calculateTeam(
            settings,
            opponentRoster,
            realMatches
        )
    };
}
